import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import SnackbarNotifier from './modules/root/SnackbarNotifier';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import AuthActions from './store/auth/actions';
import * as localStorageService from './service/localStorage/localStorageService';
import { initializeInterceptor } from './service/http/interceptor';
import GeneralThunks from './store/general/thunk';
import AuthThunks from './store/auth/thunk';
import { useAppDispatch, useAppSelector } from './@types/redux';
import { useLocation } from 'react-router-dom';
import { AnimatedLoader, IGoogleLoginError, IGoogleLoginSuccess, Login } from '@zz2/zz2-ui';
import navActions from './store/nav/actions';
import EnvHelper from './service/helper/envHelper';
import { QUERY_DATA_STALE_TIME } from './appConstants';
import { IUserToken } from './@types/model/auth/userToken/userToken';
import { VERSION } from './version';
import { ThemeProvider } from '@mui/material';
import NavFrame from './modules/root/NavFrame';
import defaultTheme from './style/materialTheme';
import RightActions from './store/rights/actions';

const App = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const isLoggingIn = useAppSelector<boolean>(x => x.auth.isLoggingIn);
    const isLoggingOut = useAppSelector<boolean>(x => x.auth.isLoggingOut);
    const session = useAppSelector<IUserToken | null>(x => x.auth.session);
    const selectedResponsibleEmployeeId = useAppSelector(state => state.right.selectedResponsibleEmployeeId);
    const showRecoveryScreen = location.pathname.includes('/reset-password');

    const ReactQueryDevtoolsProduction = React.lazy(() =>
        import('@tanstack/react-query-devtools/build/lib/index.prod.js').then(
            (d) => ({
                default: d.ReactQueryDevtools,
            }),
        ),
    );

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        dispatch(RightActions.setSelectedResponsibleEmployeeId(null));
    }, [location]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const loadData = () : void => {
        dispatch(AuthActions.setLoggingIn(true));
        localStorageService.onSessionChanged((user) => {

            if (user) { 
                /* Logged In */
                initializeInterceptor(user.token, onUnauthenticated, onUnauthorized, onConnectionError);
                dispatch(AuthActions.setSession(user));
                dispatch(AuthActions.setToken(user.token));
                dispatch(AuthActions.setLoggedIn(true));
                dispatch(AuthActions.setLoggingIn(false));

            } else { 
                /* Logged Out or Not yet logged in */
                initializeInterceptor('', onUnauthenticated, onUnauthorized, onConnectionError);
                dispatch(AuthActions.setLoggedIn(false));
                dispatch(AuthActions.setSession(null));
                dispatch(AuthActions.setLoggingIn(false));
            }
        });
    };

    const manualLogIn = async (username : string, password : string) : Promise<void> => {
        dispatch(AuthThunks.manualLogIn({ username, password }));
    };

    const manualSignUp = async (employeeNumber : string, password : string) : Promise<void> => {
        dispatch(AuthThunks.signUp({ employeeNumber, password, primaryImei: '' }));
    };

    const onGoogleLogInSuccess = async (response : IGoogleLoginSuccess) : Promise<void> => {
        await dispatch(AuthThunks.googleLogIn({
            code: response.code,
        })).unwrap();

        navActions.navReplace('/home');
    };

    const requestForgottenPassword = async () : Promise<void> => {
        return;
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const onUnauthenticated = (error : any) : void => {
        initializeInterceptor('', onUnauthenticated, onUnauthorized, onConnectionError);
        dispatch(GeneralThunks.showErrorSnackbar({
            defaultMessage: 'Unauthenticated.',
            ex: error,
        }));

        dispatch(AuthThunks.logout());
    };

    const onUnauthorized = (error : any) : void => {
        dispatch(GeneralThunks.showErrorSnackbar({
            defaultMessage: 'Insufficient rights.',
            ex: error,
        }));
    };

    const onConnectionError = () : void => {
        dispatch(GeneralThunks.showErrorSnackbar({
            defaultMessage: 'Connection error.',
        }));
    };

    const onGoogleLogInFailure = (response : IGoogleLoginError) : void => {
        dispatch(GeneralThunks.showErrorSnackbar({
            defaultMessage: response.error ?? 'Login Error',
            ex: response,
        }));
        dispatch(AuthActions.setLoggingIn(false));
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const renderApp = useMemo(() => {
        if (isLoggingIn || isLoggingOut) {
            return <AnimatedLoader/>;
        }
        
        if (session) {
            return (
                <NavFrame />
            );
        }

        return showRecoveryScreen 
            ? <div/>
            //? <PasswordRecovery initialFormValues={passwordResetInitialFormValues()} /> 
            : <Login
                env={EnvHelper.getEnvName()}
                version={VERSION.version}
                isLoading={isLoggingIn}
                isLoggingIn={isLoggingIn}
                manualLogIn={manualLogIn}
                requestForgottenPassword={requestForgottenPassword}
                onGoogleSignInSuccess={onGoogleLogInSuccess}
                onGoogleSignInFailure={onGoogleLogInFailure}
                enableSignUp={true}
                manualSignUp={manualSignUp}
            />;

    }, [isLoggingIn, session, showRecoveryScreen, isLoggingOut]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/
    
    const queryClient = new QueryClient({
        defaultOptions: {
            queries: { staleTime: QUERY_DATA_STALE_TIME },
            mutations: {}
        }
    });

    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={defaultTheme}>
                { renderApp }
                <SnackbarNotifier />
            </ThemeProvider>
            <ReactQueryDevtools initialIsOpen={false} />
            <React.Suspense fallback={null}>
                {
                    EnvHelper.isDevelopmentQa() &&
                    <ReactQueryDevtoolsProduction />
                }  
            </React.Suspense>
        </QueryClientProvider>
    );
};

export default App;